/**
 * Worklist Page
 *
 * Study assignment and workflow management
 */

import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import {
  ClipboardList,
  Filter,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Loader2,
  AlertCircle,
  Clock,
  AlertTriangle,
  Image,
  UserPlus,
  Inbox,
  Undo2,
} from 'lucide-react'

import {
  useWorklist,
  useMyWorklist,
  useOverdueItems,
  useUnassignedItems,
  useReturnedItems,
  useCancelWorklistItem,
  getWorklistStatusVariant,
  isWorklistItemOverdue,
} from '@/application/worklist'
import { usePriorities } from '@/application/priorities/usePriorities'
import { PriorityBadge } from '@/presentation/components/shared/PriorityBadge'
import { useAuth } from '@/application/auth'
import { AssignWorklistModal, ReturnStudyModal, TimeIndicator } from '@/presentation/components/worklist'
import { canReturnWorklistItem } from '@/domain/worklist'
import { USER_ROLES } from '@/config/constants'
import type { WorklistFilters, WorklistItemWithPatient } from '@/domain/worklist'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import {
  WORKLIST_STATUS_LABELS,
  PAGINATION,
  MODALITY_LABELS,
  type WorklistStatus,
  type Modality,
} from '@/config/constants'

// =============================================================================
// Worklist Item Row Component
// =============================================================================

function WorklistItemRow({
  item,
  onCancel: _onCancel,
  onAssign,
  onReturn,
  onRowClick,
  isUpdating,
  showAssignedTo = false,
  canAssign = false,
}: {
  item: WorklistItemWithPatient
  onCancel: () => void
  onAssign?: () => void
  onReturn?: () => void
  onRowClick: () => void
  isUpdating: boolean
  showAssignedTo?: boolean
  canAssign?: boolean
}) {
  const isOverdue = isWorklistItemOverdue(item)
  const isUnassigned = !item.assignedTo

  // Permite navegar al visor al hacer clic en la fila
  const handleRowClick = (e: React.MouseEvent) => {
    // No navegar si se hizo clic en un botón o enlace
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('a')) {
      return
    }
    onRowClick()
  }

  return (
    <tr
      onClick={handleRowClick}
      className={`border-b border-[var(--border)] hover:bg-[var(--primary)]/5 transition-colors cursor-pointer ${
        isOverdue ? 'bg-[var(--color-error)]/10 hover:bg-[var(--color-error)]/15' : ''
      } ${item.status === 'RETURNED' ? 'bg-[var(--color-warning)]/10 hover:bg-[var(--color-warning)]/15' : ''}`}
      title={item.status === 'RETURNED' ? 'Clic para ver detalles y corregir' : 'Clic para ver imágenes'}
    >
      <td className="p-4">
        <div className="flex items-center gap-2">
          <PriorityBadge
            priorityId={item.priorityId}
            priorityNumber={item.priority}
            size="sm"
          />
          {isOverdue && (
            <span title="Vencido">
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </span>
          )}
        </div>
      </td>
      <td className="p-4">
        <div className="min-w-0">
          <p className="font-medium text-[var(--foreground)] truncate">
            {item.patientName}
          </p>
          <p className="text-xs text-[var(--foreground-muted)]">
            {item.patientDocumentType} {item.patientDocumentNumber}
          </p>
        </div>
      </td>
      <td className="p-4">
        <div className="min-w-0">
          <p className="font-medium text-[var(--foreground)] truncate">
            {item.studyDescription || MODALITY_LABELS[item.modality as Modality] || item.modality}
          </p>
          <p className="text-xs text-[var(--foreground-muted)]">
            {MODALITY_LABELS[item.modality as Modality] || item.modality} • {item.studyDate}
          </p>
        </div>
      </td>
      <td className="p-4">
        <Badge variant={getWorklistStatusVariant(item.status)}>
          {WORKLIST_STATUS_LABELS[item.status]}
        </Badge>
      </td>
      <td className="p-4">
        {item.dueAt ? (
          <TimeIndicator
            dueAt={item.dueAt}
            createdAt={item.createdAt}
            variant="badge"
            showIcon
          />
        ) : (
          <span className="text-sm text-[var(--foreground-muted)]">-</span>
        )}
      </td>
      {showAssignedTo && (
        <td className="p-4 text-sm">
          {item.assignedTo ? (
            <span className="text-[var(--foreground)]">{item.assignedTo}</span>
          ) : (
            <span className="text-[var(--foreground-muted)] italic">Sin asignar</span>
          )}
        </td>
      )}
      <td className="p-4 text-sm text-[var(--foreground-muted)]">
        {item.notes ? (
          <span title={item.notes}>
            {item.notes.length > 30 ? `${item.notes.slice(0, 30)}...` : item.notes}
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="p-4 text-right">
        <div className="flex items-center justify-end gap-2">
          {/* Assign/Reassign button - for unassigned items or returned items */}
          {canAssign && (isUnassigned || item.status === 'RETURNED') && onAssign && (
            <Button
              variant="outline"
              size="sm"
              title={item.status === 'RETURNED' ? 'Reasignar' : 'Asignar'}
              onClick={onAssign}
              disabled={isUpdating}
              className="text-[var(--primary)]"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              {item.status === 'RETURNED' ? 'Reasignar' : 'Asignar'}
            </Button>
          )}
          {/* Primary action: Open viewer (auto-starts the worklist item) */}
          <Link to={`/viewer/${item.studyId}`}>
            <Button
              variant={item.status === 'PENDING' ? 'default' : 'ghost'}
              size="sm"
              title={item.status === 'PENDING' ? 'Abrir y comenzar' : 'Ver imágenes'}
            >
              <Image className="h-4 w-4" />
              {item.status === 'PENDING' && <span className="ml-1">Revisar</span>}
            </Button>
          </Link>
          {/* Return button - only if item can be returned */}
          {canReturnWorklistItem(item.status) && onReturn && (
            <Button
              variant="ghost"
              size="sm"
              title="Devolver estudio"
              onClick={onReturn}
              disabled={isUpdating}
            >
              <Undo2 className="h-4 w-4 text-yellow-500" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

/**
 * La misma fila, en tarjeta, para pantallas estrechas.
 *
 * POR QUE NO BASTA CON `overflow-x-auto`
 * ======================================
 * La tabla tiene ocho columnas. En una tableta en vertical eso obliga a
 * deslizar en horizontal para leer una sola fila, y el tecnologo trabaja de
 * pie, junto al equipo, con la tableta en una mano. Desplazar lateralmente
 * para ver a que paciente pertenece un estudio no es un inconveniente
 * estetico: es no poder usarlo.
 *
 * Se reparte la misma informacion en dos ejes en vez de uno, con lo
 * importante arriba (paciente y vencimiento) y las acciones abajo, del
 * tamano que se puede tocar con el pulgar.
 */
function WorklistItemCard({
  item,
  onAssign,
  onReturn,
  onRowClick,
  isUpdating,
  canAssign = false,
}: {
  item: WorklistItemWithPatient
  onAssign?: () => void
  onReturn?: () => void
  onRowClick: () => void
  isUpdating: boolean
  canAssign?: boolean
}) {
  const isOverdue = isWorklistItemOverdue(item)
  const isUnassigned = !item.assignedTo

  const alTocar = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('a')) return
    onRowClick()
  }

  return (
    <div
      onClick={alTocar}
      className={`border-b border-[var(--border)] p-4 active:bg-[var(--primary)]/5 transition-colors cursor-pointer ${
        isOverdue ? 'bg-[var(--color-error)]/10' : ''
      } ${item.status === 'RETURNED' ? 'bg-[var(--color-warning)]/10' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[var(--foreground)] truncate">
            {item.patientName}
          </p>
          <p className="text-xs text-[var(--foreground-muted)]">
            {item.patientDocumentType} {item.patientDocumentNumber}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <PriorityBadge priorityId={item.priorityId} priorityNumber={item.priority} size="sm" />
          {isOverdue && (
            <span title="Vencido">
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </span>
          )}
        </div>
      </div>

      <p className="mt-2 text-sm text-[var(--foreground)] truncate">
        {item.studyDescription || MODALITY_LABELS[item.modality as Modality] || item.modality}
      </p>
      <p className="text-xs text-[var(--foreground-muted)]">
        {MODALITY_LABELS[item.modality as Modality] || item.modality} • {item.studyDate}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge variant={getWorklistStatusVariant(item.status)}>
          {WORKLIST_STATUS_LABELS[item.status]}
        </Badge>
        {item.dueAt && (
          <TimeIndicator dueAt={item.dueAt} createdAt={item.createdAt} variant="badge" showIcon />
        )}
        {isUnassigned && (
          <span className="text-xs italic text-[var(--foreground-muted)]">Sin asignar</span>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Link to={`/viewer/${item.studyId}`} className="flex-1">
          <Button
            variant={item.status === 'PENDING' ? 'default' : 'outline'}
            size="sm"
            className="w-full"
          >
            <Image className="h-4 w-4 mr-1" />
            {item.status === 'PENDING' ? 'Revisar' : 'Ver imágenes'}
          </Button>
        </Link>
        {canAssign && (isUnassigned || item.status === 'RETURNED') && onAssign && (
          <Button variant="outline" size="sm" onClick={onAssign} disabled={isUpdating}>
            <UserPlus className="h-4 w-4" />
          </Button>
        )}
        {canReturnWorklistItem(item.status) && onReturn && (
          <Button variant="ghost" size="sm" onClick={onReturn} disabled={isUpdating} title="Devolver">
            <Undo2 className="h-4 w-4 text-yellow-500" />
          </Button>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function WorklistPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read initial tab from URL, default to 'active'
  const initialTab = (searchParams.get('tab') as 'all' | 'active' | 'completed' | 'overdue' | 'unassigned' | 'returned') || 'active'
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'completed' | 'overdue' | 'unassigned' | 'returned'>(initialTab)
  const [showFilters, setShowFilters] = useState(false)

  // Modal state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<WorklistItemWithPatient | null>(null)

  // Auth context to check user role
  const { user } = useAuth()
  const canAssignStudies =
    user?.role === USER_ROLES.ADMIN ||
    user?.role === USER_ROLES.SUPER_ADMIN ||
    user?.role === USER_ROLES.TECHNICIAN

  // Priority data for dynamic colors
  const { data: priorities } = usePriorities()

  // Filters state from URL
  const [filters, setFilters] = useState<WorklistFilters>({
    status: (searchParams.get('status') as WorklistStatus) || undefined,
    priority: searchParams.get('priority')
      ? (parseInt(searchParams.get('priority')!, 10) as 1 | 2 | 3 | 4)
      : undefined,
    limit: PAGINATION.DEFAULT_LIMIT,
    offset: parseInt(searchParams.get('offset') || '0', 10),
  })

  // Queries - only fetch unassigned if user can assign studies
  const worklistQuery = useWorklist(activeTab === 'all' ? filters : undefined)
  const myWorklistQuery = useMyWorklist()
  const overdueQuery = useOverdueItems()
  const unassignedQuery = useUnassignedItems({ enabled: canAssignStudies })
  const returnedQuery = useReturnedItems()

  // Mutations
  const cancelMutation = useCancelWorklistItem()

  const isUpdating = cancelMutation.isPending

  // Get current data based on active tab
  const getCurrentData = () => {
    const allMyItems = myWorklistQuery.data || []

    switch (activeTab) {
      case 'active':
        // Only show PENDING and IN_PROGRESS items
        const activeItems = allMyItems.filter(
          (i) => i.status === 'PENDING' || i.status === 'IN_PROGRESS'
        )
        return {
          items: activeItems,
          total: activeItems.length,
          isLoading: myWorklistQuery.isLoading,
          isError: myWorklistQuery.isError,
          error: myWorklistQuery.error,
        }
      case 'completed':
        // Only show COMPLETED items
        const completedItems = allMyItems.filter((i) => i.status === 'COMPLETED')
        return {
          items: completedItems,
          total: completedItems.length,
          isLoading: myWorklistQuery.isLoading,
          isError: myWorklistQuery.isError,
          error: myWorklistQuery.error,
        }
      case 'overdue':
        return {
          items: overdueQuery.data || [],
          total: overdueQuery.data?.length || 0,
          isLoading: overdueQuery.isLoading,
          isError: overdueQuery.isError,
          error: overdueQuery.error,
        }
      case 'unassigned':
        return {
          items: unassignedQuery.data || [],
          total: unassignedQuery.data?.length || 0,
          isLoading: unassignedQuery.isLoading,
          isError: unassignedQuery.isError,
          error: unassignedQuery.error,
        }
      case 'returned':
        return {
          items: returnedQuery.data || [],
          total: returnedQuery.data?.length || 0,
          isLoading: returnedQuery.isLoading,
          isError: returnedQuery.isError,
          error: returnedQuery.error,
        }
      default:
        return {
          items: worklistQuery.data?.items || [],
          total: worklistQuery.data?.total || 0,
          isLoading: worklistQuery.isLoading,
          isError: worklistQuery.isError,
          error: worklistQuery.error,
        }
    }
  }

  // Handler for opening assign modal
  const handleOpenAssignModal = (item: WorklistItemWithPatient) => {
    setSelectedItem(item)
    setIsAssignModalOpen(true)
  }

  // Handler for opening return modal
  const handleOpenReturnModal = (item: WorklistItemWithPatient) => {
    setSelectedItem(item)
    setIsReturnModalOpen(true)
  }

  const { items, total, isLoading, isError, error } = getCurrentData()

  // Pagination (only for 'all' tab)
  const currentPage =
    Math.floor((filters.offset || 0) / (filters.limit || PAGINATION.DEFAULT_LIMIT)) + 1
  const totalPages = Math.ceil(total / (filters.limit || PAGINATION.DEFAULT_LIMIT))

  const goToPage = (page: number) => {
    const offset = (page - 1) * (filters.limit || PAGINATION.DEFAULT_LIMIT)
    setFilters((prev) => ({ ...prev, offset }))
    setSearchParams((params) => {
      params.set('offset', offset.toString())
      return params
    })
  }

  // Update filters
  const updateFilters = (newFilters: Partial<WorklistFilters>) => {
    const updated = { ...filters, ...newFilters, offset: 0 }
    setFilters(updated)

    const params = new URLSearchParams()
    if (updated.status) params.set('status', updated.status)
    if (updated.priority) params.set('priority', updated.priority.toString())
    setSearchParams(params)
  }

  const clearFilters = () => {
    setFilters({ limit: PAGINATION.DEFAULT_LIMIT, offset: 0 })
    setSearchParams({})
  }

  // Count stats from all my items (not just current tab)
  const allMyItems = myWorklistQuery.data || []
  const pendingCount = allMyItems.filter((i) => i.status === 'PENDING').length
  const inProgressCount = allMyItems.filter((i) => i.status === 'IN_PROGRESS').length
  const completedCount = allMyItems.filter((i) => i.status === 'COMPLETED').length
  const overdueCount = allMyItems.filter((i) => isWorklistItemOverdue(i)).length
  const returnedCount = returnedQuery.data?.length || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">Mi Trabajo</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestiona tus estudios asignados
          </p>
        </div>
      </div>

      {/* Tabs */}
      {/* Las seis pestanas miden 621px juntas y no caben en un telefono.
          Con `w-fit` empujaban la pagina entera y obligaban a deslizar en
          horizontal para leer cualquier fila.

          La solucion correcta para una barra de pestanas no es apilarlas ni
          esconderlas: es que se desplace DENTRO de su propio contenedor. El
          contenido ancho se desplaza en su caja; la pagina, nunca.

          `shrink-0` en cada pestana evita que el flex las comprima hasta
          hacerlas ilegibles en vez de permitir el desplazamiento. */}
      <div className="flex items-center gap-1 bg-[var(--secondary)] p-1 rounded-lg w-fit max-w-full overflow-x-auto">
        {/* Activos - Primary tab */}
        <button
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'active'
              ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
              : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
          }`}
          onClick={() => {
            setActiveTab('active')
            setSearchParams((params) => { params.set('tab', 'active'); return params })
          }}
        >
          Activos
          {(pendingCount + inProgressCount) > 0 && (
            <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === 'active'
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                : 'bg-[var(--foreground-muted)]/20 text-[var(--foreground-muted)]'
            }`}>
              {pendingCount + inProgressCount}
            </span>
          )}
        </button>

        {/* Completados */}
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'completed'
              ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
              : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
          }`}
          onClick={() => {
            setActiveTab('completed')
            setSearchParams((params) => { params.set('tab', 'completed'); return params })
          }}
        >
          {WORKLIST_STATUS_LABELS.COMPLETED}s
          {completedCount > 0 && (
            <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === 'completed'
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                : 'bg-[var(--foreground-muted)]/20 text-[var(--foreground-muted)]'
            }`}>
              {completedCount}
            </span>
          )}
        </button>

        {/* Vencidos - with warning indicator */}
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
            activeTab === 'overdue'
              ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
              : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
          }`}
          onClick={() => {
            setActiveTab('overdue')
            setSearchParams((params) => { params.set('tab', 'overdue'); return params })
          }}
        >
          <AlertTriangle className={`h-4 w-4 ${overdueCount > 0 ? 'text-red-500' : ''}`} />
          Vencidos
          {overdueCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
              {overdueCount}
            </span>
          )}
        </button>

        {/* Sin Asignar - only for admin/technician */}
        {canAssignStudies && (
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
              activeTab === 'unassigned'
                ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
                : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
            }`}
            onClick={() => {
              setActiveTab('unassigned')
              setSearchParams((params) => { params.set('tab', 'unassigned'); return params })
            }}
          >
            <Inbox className="h-4 w-4" />
            Sin Asignar
            {unassignedQuery.data && unassignedQuery.data.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
                {unassignedQuery.data.length}
              </span>
            )}
          </button>
        )}

        {/* Devueltos */}
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
            activeTab === 'returned'
              ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
              : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
          }`}
          onClick={() => {
            setActiveTab('returned')
            setSearchParams((params) => { params.set('tab', 'returned'); return params })
          }}
        >
          <Undo2 className="h-4 w-4" />
          Devueltos
          {returnedCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
              {returnedCount}
            </span>
          )}
        </button>

        {/* Separator */}
        <div className="w-px h-6 bg-[var(--border)] mx-1" />

        {/* Todos - at the end */}
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'all'
              ? 'bg-[var(--background)] text-[var(--foreground)] shadow-sm'
              : 'text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background)]/50'
          }`}
          onClick={() => {
            setActiveTab('all')
            setSearchParams((params) => { params.set('tab', 'all'); return params })
          }}
        >
          Todos
        </button>
      </div>

      {/* Filters (only for 'all' tab) */}
      {activeTab === 'all' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filtros
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status */}
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Estado
                  </label>
                  <select
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                    value={filters.status || ''}
                    onChange={(e) =>
                      updateFilters({
                        status: (e.target.value as WorklistStatus) || undefined,
                      })
                    }
                  >
                    <option value="">Todos</option>
                    {Object.entries(WORKLIST_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <label className="text-sm font-medium text-[var(--foreground-muted)] mb-1 block">
                    Prioridad
                  </label>
                  <select
                    className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                    value={filters.priority?.toString() || ''}
                    onChange={(e) =>
                      updateFilters({
                        priority: e.target.value
                          ? (parseInt(e.target.value, 10) as 1 | 2 | 3 | 4)
                          : undefined,
                      })
                    }
                  >
                    <option value="">Todas</option>
                    {priorities?.map((p) => (
                      <option key={p.id} value={p.sortOrder}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear */}
                <div className="flex items-end">
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    Limpiar filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Contadores rapidos.
          A 768px pasaban a cuatro columnas, pero ahi el menu lateral ya ocupa
          256px y solo quedan 512 de contenido: las cuatro tarjetas no caben y
          empujaban la pagina 6px hacia fuera.

          Se retrasa el salto a cuatro columnas hasta `lg`, que es cuando hay
          sitio de verdad. Y `min-w-0` en cada tarjeta porque una celda de
          rejilla no baja del ancho de su contenido por defecto: sin eso, el
          texto largo sigue empujando aunque sobren columnas. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg shrink-0">
                <ClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)] truncate">{WORKLIST_STATUS_LABELS.PENDING}s</p>
                <p className="text-xl font-semibold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg shrink-0">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)] truncate">{WORKLIST_STATUS_LABELS.IN_PROGRESS}</p>
                <p className="text-xl font-semibold">{inProgressCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg shrink-0">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)] truncate">{WORKLIST_STATUS_LABELS.COMPLETED}s</p>
                <p className="text-xl font-semibold">{completedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">Vencidos</p>
                <p className="text-xl font-semibold">{overdueCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-500">{error?.message || 'Error al cargar worklist'}</p>
            </div>
          ) : !items.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
              <p className="text-[var(--foreground-muted)]">No hay items en la lista</p>
            </div>
          ) : (
            <>
              {/* Tarjetas en pantalla estrecha: la tabla tiene ocho columnas
                  y deslizarla en horizontal hace la lista inservible en la
                  tableta con la que trabaja el tecnologo. */}
              <div className="md:hidden">
                {(items as WorklistItemWithPatient[]).map((item) => (
                  <WorklistItemCard
                    key={item.id}
                    item={item}
                    onAssign={() => handleOpenAssignModal(item)}
                    onReturn={() => handleOpenReturnModal(item)}
                    onRowClick={() =>
                      navigate(
                        item.status === 'RETURNED'
                          ? `/studies/${item.studyId}`
                          : `/viewer/${item.studyId}`
                      )
                    }
                    isUpdating={isUpdating}
                    canAssign={canAssignStudies}
                  />
                ))}
              </div>

              {/* Tabla, de tableta en horizontal hacia arriba */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--background-subtle)]">
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Prioridad
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Paciente
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Estudio
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Estado
                      </th>
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Vencimiento
                      </th>
                      {(activeTab === 'all' || activeTab === 'unassigned') && canAssignStudies && (
                        <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                          Asignado a
                        </th>
                      )}
                      <th className="text-left p-4 font-medium text-[var(--foreground-muted)]">
                        Notas
                      </th>
                      <th className="text-right p-4 font-medium text-[var(--foreground-muted)]">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(items as WorklistItemWithPatient[]).map((item) => (
                      <WorklistItemRow
                        key={item.id}
                        item={item}
                        onCancel={() => cancelMutation.mutate(item.id)}
                        onAssign={() => handleOpenAssignModal(item)}
                        onReturn={() => handleOpenReturnModal(item)}
                        onRowClick={() => navigate(item.status === 'RETURNED' ? `/studies/${item.studyId}` : `/viewer/${item.studyId}`)}
                        isUpdating={isUpdating}
                        showAssignedTo={(activeTab === 'all' || activeTab === 'unassigned') && canAssignStudies}
                        canAssign={canAssignStudies}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination (only for 'all' tab) */}
              {activeTab === 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-[var(--border)]">
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Mostrando {(filters.offset || 0) + 1} -{' '}
                    {Math.min(
                      (filters.offset || 0) + (filters.limit || PAGINATION.DEFAULT_LIMIT),
                      total
                    )}{' '}
                    de {total} items
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-[var(--foreground-muted)]">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Assign Modal */}
      <AssignWorklistModal
        open={isAssignModalOpen}
        onOpenChange={setIsAssignModalOpen}
        item={selectedItem}
        onSuccess={() => {
          // Modal will close automatically
          // Queries will be invalidated by the mutation
        }}
      />

      {/* Return Study Modal */}
      <ReturnStudyModal
        open={isReturnModalOpen}
        onOpenChange={setIsReturnModalOpen}
        item={selectedItem}
        onSuccess={() => {
          // Modal will close automatically
          // Queries will be invalidated by the mutation
        }}
      />
    </div>
  )
}
